// TextCustomizePanel — the ENTIRE tweak-everything surface for text, shared
// by the sidebar (TextStyleTab) and the Styles Explorer modal's right rail.
//
// TWO LAYOUTS from one source of truth (2026-07-19 accordion sidebar):
//   • GROUPED (sidebar): pass `openSection` + `onToggleSection` and EVERY
//     section renders as a TbAccordion in the host's single exclusive group
//     (TextStyleTab owns the state so its own Styles accordion and these
//     share one at-most-one-open rule). All collapsed on a fresh selection;
//     headers carry an ACTIVE dot when the section holds live non-default
//     state (derived from this instance's mirrors — no new engine calls).
//     Order: Effects, Color, Raised & lowered, Fit, ELA tools, Shape,
//     Highlight, Spacing & letters, More. The Effects body ALSO hosts the
//     Bevel / 3D depth block (owner, 2026-07-19: "remove the add depth
//     section and add the bevel and 3d to the effects accordion with the
//     rest of them") — below the tiles + option cards. The Transparency
//     slider is GONE from both layouts (R1 ribbon, 2026-07-20): Row 2's
//     Opacity is its one canonical home under the no-twins law.
//     The Emphasize accordion is GONE (owner, 2026-07-19: "should not be
//     there at all") — the standard controls are selection-aware instead
//     (engine routeToLiveSelection; Reset character styling lives in
//     FillSection).
//   • FLAT (styles modal rail, no group props): the original layout —
//     Raised & lowered/Fit and ELA-tools blocks, Customize tiles, Color
//     section, and the self-managed exclusive Shape/Highlight/Spacing/More
//     accordions. The modal never had Add depth, so it does NOT gain the
//     depth block.
//
// The panel instantiates its OWN useSelectionProps (multi-instance safe by
// design — both re-mirror on object:modified, redundant commits dedupe), so
// it works anywhere it's mounted without threading objProps through props.
//
// Props:
//   editor / selectedObjects — same pair every inspector surface receives
//   dense    — reserved layout flag (v1: intentionally changes nothing)
//   onMutate — optional: called before every apply() so a host (the styles
//              modal's Revert-dirty tracking) can observe that ANY tweak
//              happened without forking the commit-mode matrix.
//   openSection / onToggleSection — grouped-mode wiring (see above): the id
//              of the open section (null = all closed) and the toggle the
//              accordion heads call. Providing onToggleSection IS the mode
//              switch.
//   onOpenExplorer — optional (category) => void: hosts that own a Styles
//              Explorer mount (TextStyleTab) pass it so the depth block's
//              quiet "More looks…" links can deep-link into the explorer at
//              the right rail category ('bevel' | '3d'). Without it the
//              links simply don't render — no dead buttons on hosts that
//              have no explorer to open.
import React, { useCallback, useRef, useState } from 'react';
import { useSelectionProps, isTextType } from '../../hooks/useSelectionProps';
import {
  TbSlider, TbColorField, TbSegmented, TbAccordion, TbSwatchRow,
} from '../../primitives/toolbarPrimitives';
import { PaintField } from '../../primitives/PaintPicker';
import { FX_BEVEL_DEFAULTS } from '../../lib/fx/fxBevel.js';
import { FX_EXTRUDE_DEFAULTS } from '../../lib/fx/fxExtrude.js';
import { EFFECT_DEFS, EFFECT_ORDER } from './effectDefs';
import EffectTiles from './EffectTiles';
import DepthTileFace from './depthTileFaces';
import EffectOptions from './EffectOptions';
import FillSection from './FillSection';
import TextureExplorerModal from './TextureExplorerModal';
import RichTextControls from './RichTextControls';
import PhonicsControls from './PhonicsControls';

const HIGHLIGHT_SWATCHES = ['#fde047', '#86efac', '#93c5fd', '#f9a8d4', '#fdba74', '#ffffff', '#0f172a'];

// Sticker plate defaults (the 'match letters' fill). Plate + sides paint is
// the house PaintField now (owner's one-standard law, 2026-07-20): solid +
// texture inline — the engine contract for both is null | '#hex' | a bare
// manifest texture id (fxExtrude plate.fill / sideFill).
const PLATE_DEFAULTS = { enabled: true, width: 50, depth: 40, fill: null };

/** hex|textureId|null (the fxExtrude string encoding) → PaintField value. */
const extrudePaintValue = (v) =>
  (v == null ? null : (String(v).startsWith('#') ? v : { type: 'texture', id: v }));

// ---- light-shape-finish lane (2026-07-19) ---------------------------------
// Bevel shape → params.technique. Rounded (smooth) = balloon at high width;
// Chiseled (chisel) = the owner's "protruding edge" (linear facets + mitre
// ridge — same profile family as the baked engine's chisel).
const BEVEL_SHAPE_OPTIONS = [
  { value: 'smooth', label: 'Rounded' },
  { value: 'chisel', label: 'Chiseled' },
];
// Bevel finish → params.finish (owner 2026-07-20). The 2D surface family,
// anchored to the baked style bundles (fxBevel FINISHES). Distinct from the 3D
// archetype FINISH_OPTIONS below; satin = the pre-finish look (byte-stable).
const BEVEL_FINISH_OPTIONS = [
  { value: 'matte', label: 'Matte' },
  { value: 'satin', label: 'Satin' },
  { value: 'glossy', label: 'Glossy' },
  { value: 'metal', label: 'Metal' },
];
// 3D finish → params.archetype. Educator labels; internal ids UNCHANGED
// (saved recipes/explorer presets keep candy-gloss/metal-foil).
const FINISH_OPTIONS = [
  { value: 'matte', label: 'Matte' },
  { value: 'satin', label: 'Satin' },
  { value: 'candy-gloss', label: 'Glossy' },
  { value: 'metal-foil', label: 'Metal' },
];
// Light source → params.lightMode (both fx): Custom = the object's own
// angle/preset; Poster = the document's ONE shared sun (workspace data).
const LIGHT_MODE_OPTIONS = [
  { value: 'custom', label: 'Custom' },
  { value: 'poster', label: 'Poster' },
];
// Compact preset select (Custom mode) → params.lightPreset, existing ids.
const LIGHT_PRESET_OPTIONS = [
  { value: 'studio', label: 'Studio' },
  { value: 'sunset-rim', label: 'Sunset' },
  { value: 'neon-dark', label: 'Neon' },
  { value: 'soft-top', label: 'Soft' },
];

// Small circular "sun position" dial — the EmbossPanel LightDial pattern
// (same convention: 0° = light from the right, CCW, dot sits where the light
// comes FROM), compacted for the dense depth block and wired for the house
// commit grammar: drag ticks call onDrag (the caller defers), release calls
// onCommit (ONE history entry per drag), arrow keys step 5° via onKey.
function PosterLightDial({ angle, onDrag, onCommit, onKey }) {
  const ref = useRef(null);
  const draggingRef = useRef(false);
  const compute = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    let deg = (Math.atan2(-(e.clientY - cy), e.clientX - cx) * 180) / Math.PI;
    if (deg < 0) deg += 360;
    onDrag(Math.round(deg));
  }, [onDrag]);
  const onDown = (e) => {
    draggingRef.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    compute(e);
  };
  const onMove = (e) => { if (draggingRef.current) compute(e); };
  const onUp = (e) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* ignore */ }
    onCommit();
  };
  const rad = (angle * Math.PI) / 180;
  const hx = 50 + 36 * Math.cos(rad);
  const hy = 50 - 36 * Math.sin(rad);
  return (
    <div className="cce-poster-dial-wrap">
      <div
        ref={ref}
        className="cce-poster-dial"
        data-poster-dial
        role="slider"
        aria-label="Poster light from"
        aria-valuemin={0}
        aria-valuemax={360}
        aria-valuenow={Math.round(angle)}
        tabIndex={0}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { e.preventDefault(); onKey((Math.round(angle) + 355) % 360); }
          else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { e.preventDefault(); onKey((Math.round(angle) + 5) % 360); }
        }}
      >
        <span className="cce-poster-dial-dot" style={{ left: `${hx}%`, top: `${hy}%` }} />
      </div>
      <span className="cce-poster-dial-label">Sun position</span>
    </div>
  );
}

// (CASE_OPTIONS / LIST_OPTIONS left with the Case and List rows — R2 moved
// both controls to Row 1 of the ribbon, their one canonical home.)

// Shape tiles beside Straight. `amount` is the shape's starting bend — a tile
// only applies it when it is not already the active shape, so re-clicking the
// live one never resets the slider. The arc keeps the label "Curve": it is the
// word teachers reach for, and the probe suite navigates by it.
const CURVE_TILES = [
  {
    value: 'arc',
    label: 'Curve',
    amount: 50,
    previewStyle: { borderBottom: '2px solid #94a3b8', borderRadius: '0 0 50% 50%', paddingBottom: 2 },
  },
  {
    value: 'circle',
    label: 'Circle',
    amount: 100,
    previewStyle: { border: '2px solid #94a3b8', borderRadius: '50%', padding: '2px 4px' },
  },
  {
    value: 'wave',
    label: 'Wave',
    amount: 50,
    previewStyle: { borderBottom: '2px solid #94a3b8', borderRadius: '50% 0 50% 0', paddingBottom: 2 },
  },
];

const BLEND_OPTIONS = [
  { value: 'source-over', label: 'Normal' },
  { value: 'multiply', label: 'Darker' },
  { value: 'screen', label: 'Lighter' },
  { value: 'overlay', label: 'Vivid' },
];

// Effects that cannot mean anything on a 3D (extruded) object — each decided
// by TRYING it (2026-07-17 3D-controls lane), not assumed:
//   hollow / trace — both blank the fill to show the poster through the
//     letters; a 3D letter is a solid slab, there is nothing to see through.
//   sticker — its die-cut border is a 2D stroke the bake replaces; the 3D
//     answer is the real extruded plate ("Sticker plate" in the depth block
//     at the bottom of Effects).
// Shadow/Lift/Glow/Echo compose natively around the baked silhouette and
// Outline renders as a real rim on the 3D — none of them are gated.
const GATED_3D_TILES = new Set(['hollow', 'trace', 'sticker']);

const hexOr = (v, fallback) => (typeof v === 'string' && v.startsWith('#') ? v : fallback);

function Section({ title, children }) {
  return (
    <div className="cce-inspector-section">
      <h4>{title}</h4>
      {children}
    </div>
  );
}

export default function TextCustomizePanel({
  editor, selectedObjects, dense = false, onMutate, openSection, onToggleSection, onOpenExplorer,
}) {
  const { objProps, apply: applyBase, commitNow } = useSelectionProps(editor, selectedObjects);
  // Same signature/behavior as the raw apply — onMutate is a pre-notify only.
  const apply = useCallback((fn, mode) => {
    onMutate?.();
    return applyBase(fn, mode);
  }, [applyBase, onMutate]);
  void dense; // reserved (v1: no layout differences between hosts)

  // Exclusive accordion group. GROUPED mode: the host owns the state (one
  // group spanning TextStyleTab's sections + all of these). FLAT mode: the
  // original self-managed group covering Shape/Highlight/Spacing/More only.
  const grouped = typeof onToggleSection === 'function';
  const [selfOpenSection, setSelfOpenSection] = useState(null);
  const currentOpen = grouped ? openSection : selfOpenSection;
  const toggleSection = grouped
    ? onToggleSection
    : (id) => setSelfOpenSection((s) => (s === id ? null : id));
  const acc = (id) => ({
    open: currentOpen === id,
    onToggle: () => toggleSection(id),
  });

  // One option card per ACTIVE effect (sticker occupies both slots — dedupe)
  const fx = objProps.textEffects || {};
  const activeEntries = [];
  const seen = new Set();
  for (const slot of ['shadow', 'stroke']) {
    const entry = fx[slot];
    if (entry?.id && EFFECT_DEFS[entry.id] && !seen.has(entry.id)) {
      seen.add(entry.id);
      activeEntries.push(entry);
    }
  }
  activeEntries.sort((a, b) => EFFECT_ORDER.indexOf(a.id) - EFFECT_ORDER.indexOf(b.id));

  const highlight = objProps.highlightRecipe;
  const curveAmount = objProps.curveAmount ?? 0;
  const curved = curveAmount !== 0;
  const curveShape = objProps.curveShape ?? 'arc';
  const curveBlocked = objProps.curveBlocked ?? null;
  const editing = !!objProps.isEditing;
  // 3D (fx-extrude) selection: gate the controls that cannot apply to a baked
  // 3D raster — DISABLED with a plain-words hint, never silent (owner: "all
  // the other controls seem to be useless" — the useless ones must say why).
  const is3d = !!objProps.is3dActive;
  // The panel is also hosted by surfaces that can hold a non-text selection;
  // the per-character text sections are meaningless there.
  const isText = isTextType(selectedObjects?.[0]?.type)
    || isTextType(editor?.canvas?.getActiveObject()?.type);

  // Depth (Bevel / 3D) checkbox truth = the mirrors, never local state:
  // patchTextFx drops a step at depth 0, so depth>0 ⇔ the step exists in
  // data.fx. (The block itself renders in GROUPED mode only — see below.)
  const bevelOn = (objProps.fxBevelDepth ?? 0) > 0;
  const extrudeOn = (objProps.fxExtrudeDepth ?? 0) > 0;
  // Sides / plate texture browsers (3D sideFill, plate.fill) — top-level
  // state, the depth block renders inside an IIFE where hooks can't live.
  const [sidesTexOpen, setSidesTexOpen] = useState(false);
  const [plateTexOpen, setPlateTexOpen] = useState(false);
  // ONE history entry per tick either way: apply(fn,'now') commits once.
  const toggleBevel = (checked) =>
    apply(() => editor?.patchTextFx('bevel', { depth: checked ? FX_BEVEL_DEFAULTS.depth : 0 }), 'now');
  const toggle3d = (checked) =>
    apply(() => editor?.patchTextFx('extrude', { depth: checked ? FX_EXTRUDE_DEFAULTS.depth : 0 }), 'now');

  // ---- ACTIVE-dot rules (grouped mode) — all from this instance's mirrors.
  // Color deliberately dots only for gradient/texture: every text has SOME
  // solid color, so a solid-recolor dot would sit lit on half the posters —
  // noise, not signal. Fit never dots: it is a one-shot action, not state.
  const dots = {
    // Effects hosts the depth block, so its dot covers a slot effect or
    // either depth > 0. The opacity term left with the Transparency slider
    // (R1 no-twins: Row 2's Opacity is the one canonical home and carries
    // its own visibility). More keeps blend only.
    effects: !!(fx.shadow?.id || fx.stroke?.id) || bevelOn || extrudeOn,
    color: ['linear-gradient', 'radial-gradient', 'texture'].includes(objProps.textFill?.type),
    script: !!objProps.hasScriptStyles,
    fit: false,
    phonics: (objProps.phonicsMode ?? 'none') !== 'none',
    shape: curved,
    highlight: !!highlight,
    // Case/List terms left with their rows (R2 — Row 1 owns both now): a dot
    // must only claim state the section itself can explain.
    spacing: (objProps.charSpacing ?? 0) !== 0
      || Math.abs((objProps.lineHeight ?? 1.16) - 1.16) > 0.001,
    more: (objProps.blendMode ?? 'source-over') !== 'source-over',
  };

  // ---- section contents (shared verbatim by both layouts) ----
  const effectsContent = (
    <>
      <EffectTiles
        objProps={objProps}
        apply={apply}
        editor={editor}
        gatedIds={is3d ? GATED_3D_TILES : null}
      />
      {is3d && (
        <div className="cce-hint cce-hint-3d-gate">
          3D letters are solid, so Hollow and Trace need flat text (untick 3D
          under Effects). For a sticker border in 3D, turn on “Sticker
          plate” under Effects.
        </div>
      )}
      {activeEntries.map((entry) => (
        <EffectOptions
          key={entry.id}
          def={EFFECT_DEFS[entry.id]}
          entry={entry}
          objProps={objProps}
          apply={apply}
          commitNow={commitNow}
          editor={editor}
        />
      ))}
    </>
  );

  const colorContent = (
    <FillSection objProps={objProps} apply={apply} commitNow={commitNow} editor={editor} />
  );

  const shapeContent = (
    <>
      <div className="cce-fx-grid cce-fx-grid-2">
        <button
          type="button"
          className={`cce-fx-tile${!curved ? ' active' : ''}`}
          disabled={editing}
          onClick={() => apply(() => editor?.changeTextCurve(0), 'now')}
        >
          <span className="cce-fx-tile-preview">Aa</span>
          <span className="cce-fx-tile-label">Straight</span>
        </button>
        {CURVE_TILES.map((tile) => {
          const active = curved && curveShape === tile.value;
          return (
            <button
              key={tile.value}
              type="button"
              className={`cce-fx-tile${active ? ' active' : ''}${is3d ? ' cce-fx-tile-gated' : ''}`}
              // Straight stays reachable while blocked so a text that wrapped
              // AFTER being curved can still be flattened. 3D gating: the
              // bake lays glyphs out itself — the engine would silently DROP
              // the extrude step to curve (changeTextCurve's exclusivity
              // rule, kept for recipes/undo); a disabled tile with the hint
              // below beats silently losing the 3D the teacher just built.
              disabled={editing || is3d || (!!curveBlocked && !curved)}
              onClick={() => {
                if (!active) apply(() => editor?.changeTextCurve(tile.amount, tile.value), 'now');
              }}
            >
              <span className="cce-fx-tile-preview" style={tile.previewStyle}>Aa</span>
              <span className="cce-fx-tile-label">{tile.label}</span>
            </button>
          );
        })}
      </div>
      {is3d && !editing && (
        <div className="cce-hint">
          3D words stay straight — untick 3D under Effects to curve this text.
        </div>
      )}
      {curved && !editing && (
        <TbSlider
          label={curveShape === 'circle' ? `Wrap around: ${curveAmount}` : `Bend: ${curveAmount}`}
          value={curveAmount}
          min={-100}
          max={100}
          step={5}
          // At 0 the slider unmounts (shape flips to Straight), so its own
          // pointer-up commit can't fire — commit that tick immediately.
          onChange={(v) => apply(() => editor?.changeTextCurve(v, curveShape), v === 0 ? 'now' : 'defer')}
          onCommit={commitNow}
        />
      )}
      {curveBlocked && !editing && (
        <div className="cce-hint">
          {curveBlocked === 'break'
            ? 'Curving works on a single line — remove the line break to curve this text.'
            : 'Curving works on a single line — widen the text box (or shorten the words) to curve this text.'}
        </div>
      )}
      {editing && <div className="cce-hint">Finish editing the text to change its shape.</div>}
    </>
  );

  const highlightContent = (
    <>
      <TbSwatchRow
        colors={HIGHLIGHT_SWATCHES}
        value={highlight?.color ?? null}
        onPick={(c) => apply(() => editor?.setTextHighlight({ color: c, fade: highlight?.fade ?? 0 }), 'now')}
        onClear={() => apply(() => editor?.setTextHighlight(null), 'now')}
      />
      <div className="cce-inspector-row">
        <span>Custom</span>
        <TbColorField
          label="Highlight color"
          value={hexOr(highlight?.color, '#fde047')}
          onChange={(v) => apply(() => editor?.setTextHighlight({ color: v, fade: highlight?.fade ?? 0 }), 'soon')}
        />
      </div>
      {highlight && (
        <TbSlider
          label={`Fade: ${highlight.fade ?? 0}`}
          value={highlight.fade ?? 0}
          min={0}
          max={100}
          step={1}
          onChange={(v) => apply(() => editor?.setTextHighlight({ color: highlight.color, fade: v }), 'defer')}
          onCommit={commitNow}
        />
      )}
    </>
  );

  const spacingContent = (
    <>
      {/* UI −20..80 maps ×10 onto fabric charSpacing −200..800 */}
      <TbSlider
        label={`Letter spacing: ${Math.round((objProps.charSpacing ?? 0) / 10)}`}
        value={(objProps.charSpacing ?? 0) / 10}
        min={-20}
        max={80}
        step={1}
        onChange={(v) => apply(() => editor?.changeCharSpacing(v * 10), 'defer')}
        onCommit={commitNow}
      />
      <div className="cce-endpoints" aria-hidden="true">
        <span>Tight</span>
        <span>Wide</span>
      </div>
      <TbSlider
        label={`Line spacing: ${(objProps.lineHeight ?? 1.16).toFixed(2)}`}
        value={objProps.lineHeight ?? 1.16}
        min={0.5}
        max={3}
        step={0.05}
        onChange={(v) => apply(() => editor?.changeLineHeight(v), 'defer')}
        onCommit={commitNow}
      />
      {/* NO-TWINS (R2, concept §5): the Case and List rows moved to Row 1 of
          the ribbon (Case cycle + Lists chips — their ONE canonical home).
          The letter/line sliders above stay: they are the deep fine-tune of
          Row 1's Spacing minis (the drawer-is-the-ribbon-unfolded law). The
          wrap hint stays too — it explains a canvas behavior wherever the
          list came from. */}
      {objProps.listWraps && (
        <div className="cce-hint">
          A long item continues under its bullet, not beside it. Widen the text box to keep items on one line.
        </div>
      )}
    </>
  );

  // Transparency is GONE from this panel (R1 no-twins, concept §5 row 1):
  // Row 2's inline Opacity slider is the ONE canonical home (More → Effects
  // → Row 2 was its full journey). Both layouts lost it — the styles-modal
  // rail was the same twin under a different roof. Blend stays in More.

  const moreContent = (
    <>
      <div className="cce-inspector-row">
        <span>Blend with background</span>
      </div>
      <TbSegmented
        label="Blend with background"
        value={objProps.blendMode ?? 'source-over'}
        options={BLEND_OPTIONS}
        onChange={(v) => apply(() => editor?.changeBlendMode(v), 'now')}
      />
    </>
  );

  // ---- the depth block (GROUPED/sidebar ONLY) — the ENTIRE former "Add
  // depth" section, folded into the bottom of the Effects accordion (owner,
  // 2026-07-19: "remove the add depth section and add the bevel and 3d to
  // the effects accordion with the rest of them"). Bevel and 3D stay
  // CHECKBOXES that expose the sliders: checked ⇔ the step is live in
  // data.fx (mirror-derived, above — never local state). Checking applies
  // the step at its engine default depth (bevel 50 / 3D 55) as ONE history
  // entry via apply(fn,'now'); unchecking removes it (depth 0 — patchTextFx
  // drops the step), also one entry. Each checked group holds the depth
  // slider (defer+commitNow, unchanged), the Sticker plate block (3D), a
  // jargon-free hint, and — only when the host wires onOpenExplorer — a
  // quiet "More looks…" link into the Styles Explorer at the right
  // category. bevel+3D COMPOSE (owner, 2026-07-16): with both on, the 3D
  // render owns the look and the bevel depth drives its edge rounding;
  // untick 3D and the 2D bevel returns. The styles-modal FLAT surface never
  // had Add depth and must not gain it: this block is grouped-only.
  const canExplore = typeof onOpenExplorer === 'function';
  const depthContent = grouped && (
    <div className="cce-fx-depth">
      <label className="cce-depth-toggle cce-depth-toggle-bevel">
        <input
          type="checkbox"
          checked={bevelOn}
          onChange={(e) => toggleBevel(e.target.checked)}
        />
        <DepthTileFace kind="bevel" />
        <span>Bevel</span>
      </label>
      {bevelOn && (
        <div className="cce-depth-group">
          <TbSlider
            label={`Bevel depth: ${objProps.fxBevelDepth ?? 0}`}
            value={objProps.fxBevelDepth ?? 0}
            min={0}
            max={250}
            step={1}
            onChange={(v) => apply(() => editor?.patchTextFx('bevel', { depth: v }), 'defer')}
            onCommit={commitNow}
          />
          {/* Width = the band each edge climbs (params.size). At 100 the band
              outgrows a bold stroke's half-width, the flat face vanishes and
              the letters go fully round — Rounded reads balloon, Chiseled
              reads as the protruding edge (owner, 2026-07-19). */}
          <TbSlider
            label={`Bevel width: ${objProps.fxBevelWidth ?? 34}`}
            value={objProps.fxBevelWidth ?? 34}
            min={0}
            max={250}
            step={1}
            onChange={(v) => apply(() => editor?.patchTextFx('bevel', { size: v }), 'defer')}
            onCommit={commitNow}
          />
          <div className="cce-inspector-row cce-bevel-shape-row">
            <span>Shape</span>
            <TbSegmented
              label="Bevel shape"
              value={objProps.fxBevelShape ?? 'smooth'}
              options={BEVEL_SHAPE_OPTIONS}
              onChange={(v) => apply(() => editor?.patchTextFx('bevel', { technique: v }), 'now')}
            />
          </div>
          <div className="cce-inspector-row cce-bevel-finish-row">
            <span>Finish</span>
            <TbSegmented
              label="Bevel finish"
              value={objProps.fxBevelFinish ?? 'satin'}
              options={BEVEL_FINISH_OPTIONS}
              onChange={(v) => apply(() => editor?.patchTextFx('bevel', { finish: v }), 'now')}
            />
          </div>
          <label className="cce-poster-check cce-poster-check-bevel">
            <input
              type="checkbox"
              checked={(objProps.fxBevelLightMode ?? 'custom') === 'poster'}
              onChange={(e) => apply(
                () => editor?.patchTextFx('bevel', { lightMode: e.target.checked ? 'poster' : 'custom' }),
                'now',
              )}
            />
            <span>Use poster light</span>
          </label>
          <div className="cce-hint">
            Bevel is live — keep typing while the letters stay raised. It
            shapes the letters only: a sticker border or badge keeps its own
            “Beveled sticker” control. With 3D on, Bevel&apos;s depth, width
            and shape carve the 3D edges — front and back alike.
          </div>
          {canExplore && (
            <button
              type="button"
              className="cce-fx-more cce-depth-link cce-depth-link-bevel"
              onClick={() => onOpenExplorer('bevel')}
            >
              More looks…
            </button>
          )}
        </div>
      )}
      <label className="cce-depth-toggle cce-depth-toggle-3d">
        <input
          type="checkbox"
          checked={extrudeOn}
          onChange={(e) => toggle3d(e.target.checked)}
        />
        <DepthTileFace kind="extrude" />
        <span>3D</span>
      </label>
      {extrudeOn && (() => {
        const plate = objProps.fxExtrudePlate || { ...PLATE_DEFAULTS, enabled: false };
        const patchPlate = (changes, mode = 'defer') =>
          apply(() => editor?.patchTextFx('extrude', { plate: { ...plate, ...changes } }), mode);
        return (
          <div className="cce-depth-group">
            <TbSlider
              label={`3D depth: ${objProps.fxExtrudeDepth ?? 0}`}
              value={objProps.fxExtrudeDepth ?? 0}
              min={0}
              max={100}
              step={1}
              onChange={(v) => apply(() => editor?.patchTextFx('extrude', { depth: v }), 'defer')}
              onCommit={commitNow}
            />
            {/* Finish chips → archetype (educator labels, internal ids
                unchanged). One chip = one history entry. */}
            <div className="cce-inspector-row cce-finish-row">
              <span>Finish</span>
            </div>
            <TbSegmented
              label="3D finish"
              value={objProps.fxExtrudeFinish ?? 'satin'}
              options={FINISH_OPTIONS}
              onChange={(v) => apply(() => editor?.patchTextFx('extrude', { archetype: v }), 'now')}
            />
            {/* Sides (dome-parity lane, 2026-07-19): the extrusion walls +
                back cap's own color/texture — params.sideFill. The paint is
                the house PaintField now (solid + texture per the engine:
                null | '#hex' | manifest id); "Aa" keeps the match-the-
                letters default beside it, and Browse-all stays the explorer
                in pickMode. One pick = one history entry. */}
            {(() => {
              const sideFill = objProps.fxExtrudeSideFill ?? null;
              const sideIsTex = typeof sideFill === 'string' && !sideFill.startsWith('#');
              const setSides = (v) =>
                apply(() => editor?.patchTextFx('extrude', { sideFill: v }), 'now');
              return (
                <>
                  <div className="cce-inspector-row cce-sides-row">
                    <span>Sides</span>
                    <button
                      type="button"
                      className={`cce-fill-swatch cce-plate-match cce-sides-match${sideFill == null ? ' active' : ''}`}
                      title="Match the letters"
                      onClick={() => setSides(null)}
                    >
                      Aa
                    </button>
                    <PaintField
                      label="Sides color or texture"
                      dataField="sideFill"
                      value={extrudePaintValue(sideFill)}
                      allow={['solid', 'texture']}
                      opaque
                      allowImport={false}
                      onChange={(next) => {
                        const v = typeof next === 'string' ? next : next?.type === 'texture' ? next.id : null;
                        if (v != null) setSides(v);
                      }}
                      onCommit={commitNow}
                      onBrowseAll={() => setSidesTexOpen(true)}
                    />
                  </div>
                  {sidesTexOpen && (
                    <TextureExplorerModal
                      objProps={objProps}
                      apply={apply}
                      commitNow={commitNow}
                      editor={editor}
                      pickMode
                      pickActiveId={sideIsTex ? sideFill : null}
                      onPick={(id) => setSides(id)}
                      onClose={() => setSidesTexOpen(false)}
                    />
                  )}
                </>
              );
            })()}
            {/* Light row: Custom = this object's own preset rig; Poster = the
                document's ONE shared sun (the dial edits workspace data via
                setPosterLight — drag defers, release commits ONE entry). */}
            <div className="cce-inspector-row cce-light-row">
              <span>Light</span>
              <TbSegmented
                label="Light source"
                value={objProps.fxExtrudeLightMode ?? 'custom'}
                options={LIGHT_MODE_OPTIONS}
                onChange={(v) => apply(() => editor?.patchTextFx('extrude', { lightMode: v }), 'now')}
              />
            </div>
            {(objProps.fxExtrudeLightMode ?? 'custom') === 'custom' ? (
              <select
                className="cce-light-preset"
                aria-label="Light preset"
                value={objProps.fxExtrudeLightPreset ?? 'studio'}
                onChange={(e) => apply(() => editor?.patchTextFx('extrude', { lightPreset: e.target.value }), 'now')}
              >
                {LIGHT_PRESET_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : (
              <>
                <PosterLightDial
                  angle={objProps.posterLight?.angle ?? 135}
                  onDrag={(deg) => apply(() => editor?.setPosterLight({ angle: deg }), 'defer')}
                  onCommit={commitNow}
                  onKey={(deg) => apply(() => editor?.setPosterLight({ angle: deg }), 'soon')}
                />
                <div className="cce-hint cce-hint-poster-light">
                  All effects set to Poster light share one sun.
                </div>
              </>
            )}
            <div className="cce-plate-block">
              <label className="cce-plate-toggle">
                <input
                  type="checkbox"
                  checked={!!plate.enabled}
                  onChange={(e) => patchPlate(
                    e.target.checked ? { ...PLATE_DEFAULTS, ...plate, enabled: true } : { enabled: false },
                    'now',
                  )}
                />
                <span>Sticker plate</span>
              </label>
              {plate.enabled && (
                <>
                  <TbSlider
                    label={`Plate size: ${plate.width}`}
                    value={plate.width}
                    min={0}
                    max={200}
                    step={1}
                    onChange={(v) => patchPlate({ width: v })}
                    onCommit={commitNow}
                  />
                  <TbSlider
                    label={`Plate depth: ${plate.depth}`}
                    value={plate.depth}
                    min={0}
                    max={100}
                    step={1}
                    onChange={(v) => patchPlate({ depth: v })}
                    onCommit={commitNow}
                  />
                  {/* Plate paint — the house PaintField (solid + texture:
                      the fxExtrude plate contract is fill hex|textureId|
                      null). "Aa" keeps the match-the-letters default;
                      Browse-all opens the explorer in pickMode. */}
                  <div className="cce-inspector-row cce-plate-fill-row">
                    <span>Color</span>
                    <button
                      type="button"
                      className={`cce-fill-swatch cce-plate-match${plate.fill == null ? ' active' : ''}`}
                      title="Match the letters"
                      onClick={() => patchPlate({ fill: null }, 'now')}
                    >
                      Aa
                    </button>
                    <PaintField
                      label="Plate color or texture"
                      dataField="plateFill"
                      value={extrudePaintValue(plate.fill)}
                      allow={['solid', 'texture']}
                      onChange={(next) => {
                        const v = typeof next === 'string' ? next : next?.type === 'texture' ? next.id : null;
                        if (v != null) patchPlate({ fill: v }, 'now');
                      }}
                      onCommit={commitNow}
                      onBrowseAll={() => setPlateTexOpen(true)}
                    />
                  </div>
                  {plateTexOpen && (
                    <TextureExplorerModal
                      objProps={objProps}
                      apply={apply}
                      commitNow={commitNow}
                      editor={editor}
                      pickMode
                      pickActiveId={typeof plate.fill === 'string' && !plate.fill.startsWith('#') ? plate.fill : null}
                      onPick={(id) => patchPlate({ fill: id }, 'now')}
                      onClose={() => setPlateTexOpen(false)}
                    />
                  )}
                </>
              )}
            </div>
            <div className="cce-hint">
              3D makes the words truly solid: they go flat while you type
              and pop back when you pause, and 3D replaces any curve. Turn
              3D off and a bevel comes back as the flat-letter bevel. Drag
              the round pad by the corner to turn a 3D object.
            </div>
            {canExplore && (
              <button
                type="button"
                className="cce-fx-more cce-depth-link cce-depth-link-3d"
                onClick={() => onOpenExplorer('3d')}
              >
                More looks…
              </button>
            )}
          </div>
        );
      })()}
    </div>
  );

  // ---- GROUPED (sidebar): one accordion per section, one shared group ----
  if (grouped) {
    return (
      <>
        <TbAccordion title="Effects" dot={dots.effects} {...acc('effects')}>
          {effectsContent}
          {depthContent}
        </TbAccordion>
        <TbAccordion title="Color" dot={dots.color} {...acc('color')}>
          {colorContent}
        </TbAccordion>
        {isText && (
          <TbAccordion title="Raised & lowered" dot={dots.script} {...acc('script')}>
            <RichTextControls objProps={objProps} apply={apply} editor={editor} section="script" />
          </TbAccordion>
        )}
        {isText && (
          <TbAccordion title="Fit" dot={dots.fit} {...acc('fit')}>
            <RichTextControls objProps={objProps} apply={apply} editor={editor} section="fit" />
          </TbAccordion>
        )}
        {/* "ELA tools" (owner, 2026-07-19): the colour-coding toolset for the
            English version — future languages get their own. Internal id
            stays 'phonics' (recipes, dots, probes). */}
        {isText && (
          <TbAccordion title="ELA tools" dot={dots.phonics} {...acc('phonics')}>
            <PhonicsControls apply={apply} editor={editor} selectedObjects={selectedObjects} hideHeader />
          </TbAccordion>
        )}
        <TbAccordion title="Shape" dot={dots.shape} {...acc('shape')}>
          {shapeContent}
        </TbAccordion>
        <TbAccordion title="Highlight" dot={dots.highlight} {...acc('highlight')}>
          {highlightContent}
        </TbAccordion>
        <TbAccordion title="Spacing & letters" dot={dots.spacing} {...acc('spacing')}>
          {spacingContent}
        </TbAccordion>
        <TbAccordion title="More" dot={dots.more} {...acc('more')}>
          {moreContent}
        </TbAccordion>
      </>
    );
  }

  // ---- FLAT (styles modal rail): the original layout (minus the retired
  // Emphasize block — the standard controls are selection-aware now) ----
  return (
    <>
      {/* Raised & lowered + Fit — above the effect tiles: they act on the
          characters/box, the tiles restyle the whole object. */}
      {isText && <RichTextControls objProps={objProps} apply={apply} editor={editor} />}

      {/* ELA tools (phonics) — automatic letter colouring; re-derives
          whenever the word is retyped. */}
      {isText && (
        <PhonicsControls apply={apply} editor={editor} selectedObjects={selectedObjects} />
      )}

      {/* Customize — effect tiles + option cards (Transparency left for
          Row 2's Opacity, the one canonical home) */}
      <div className="cce-inspector-section">
        <div className="cce-fx-subhead">Customize</div>
        {effectsContent}
      </div>

      {/* Color */}
      <Section title="Color">{colorContent}</Section>

      {/* Shape */}
      <TbAccordion title="Shape" {...acc('shape')}>{shapeContent}</TbAccordion>

      {/* Highlight */}
      <TbAccordion title="Highlight" {...acc('highlight')}>{highlightContent}</TbAccordion>

      {/* Spacing & letters */}
      <TbAccordion title="Spacing & letters" {...acc('spacing')}>{spacingContent}</TbAccordion>

      {/* More */}
      <TbAccordion title="More" {...acc('more')}>{moreContent}</TbAccordion>
    </>
  );
}
